import type { Instrument } from "@trader/shared/MarketData"
import type { CandlestickData, MouseEventParams, UTCTimestamp } from "lightweight-charts"
import type { ChartSeries } from "./CandleFeed.ts"
import { DOWN, UP } from "./chartTheme.ts"

const compact = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 2 })

/**
 * Shows O / H / L / C / change / volume for the bar under the crosshair (or the
 * latest bar). Writes straight to the DOM: crosshair moves fire on every mouse
 * move, and re-rendering React for each would be wasted work.
 */
export const attachLegend = (series: ChartSeries, element: HTMLElement, instrument: Instrument): (() => void) => {
  const price = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: instrument.pricePrecision,
    maximumFractionDigits: instrument.pricePrecision
  })

  const render = (param?: MouseEventParams) => {
    const hovered = param?.seriesData.get(series.candles) as CandlestickData<UTCTimestamp> | undefined
    const bar = hovered ?? (series.candles.data().at(-1) as CandlestickData<UTCTimestamp> | undefined)
    if (bar === undefined) {
      element.textContent = ""
      return
    }
    const volume = (param?.seriesData.get(series.volume) as { value?: number } | undefined)?.value
    const change = ((bar.close - bar.open) / bar.open) * 100
    element.style.setProperty("--legend-color", bar.close >= bar.open ? UP : DOWN)
    element.textContent = [
      `O ${price.format(bar.open)}`,
      `H ${price.format(bar.high)}`,
      `L ${price.format(bar.low)}`,
      `C ${price.format(bar.close)}`,
      `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`,
      volume === undefined ? "" : `Vol ${compact.format(volume)}`
    ].join("  ")
  }

  series.chart.subscribeCrosshairMove(render)
  const dataChanged = () => render()
  series.candles.subscribeDataChanged(dataChanged)
  render()
  return () => {
    series.chart.unsubscribeCrosshairMove(render)
    series.candles.unsubscribeDataChanged(dataChanged)
  }
}
